import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { roofOrientations } from '../data/mock';
import { Calculator, Sun, Battery, Zap, Euro, ArrowRight, RefreshCw } from 'lucide-react';

const AutarkieCalculator = () => {
  const [formData, setFormData] = useState({
    consumption: '',
    orientation: '',
    roofSize: '',
    hasEV: ''
  });
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleCalculate = () => {
    if (!formData.consumption || !formData.orientation) return;
    
    setIsCalculating(true);
    
    setTimeout(() => {
      const consumption = parseInt(formData.consumption);
      const orientationFactor = roofOrientations.find(o => o.value === formData.orientation)?.factor || 1;
      const evAddition = formData.hasEV === 'yes' ? 2500 : 0;
      const totalConsumption = consumption + evAddition;
      
      // Calculate recommended PV size (roughly 1 kWp per 1000 kWh)
      const recommendedPV = Math.ceil((totalConsumption / 900) * orientationFactor * 10) / 10;
      
      // Calculate recommended battery (roughly 1 kWh per 1000 kWh consumption)
      const recommendedBattery = Math.ceil(totalConsumption / 1200);
      
      // Calculate autonomy rate
      const autonomyBase = 30 + (recommendedPV * 4);
      const batteryBonus = recommendedBattery * 3;
      const autonomyRate = Math.min(85, Math.round(autonomyBase + batteryBonus));
      
      // Calculate yearly savings
      const selfConsumptionKwh = (recommendedPV * 950 * orientationFactor) * (autonomyRate / 100);
      const yearlySavings = Math.round(selfConsumptionKwh * 0.35);
      
      setResult({
        pvSize: recommendedPV,
        batterySize: recommendedBattery,
        autonomyRate,
        yearlySavings
      });
      setIsCalculating(false);
    }, 1500);
  };

  const handleReset = () => {
    setFormData({ consumption: '', orientation: '', roofSize: '', hasEV: '' });
    setResult(null);
  };

  const scrollToContact = () => {
    document.querySelector('#kontakt')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="autarkie-rechner" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 bg-sun-orange/10 text-sun-orange rounded-full text-sm font-semibold mb-4">
            Autarkie-Rechner
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Berechnen Sie Ihre
            <span className="text-sun-orange"> Unabhängigkeit</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Ermitteln Sie in wenigen Schritten, welche Anlagengröße für Sie optimal ist 
            und wie viel Sie sparen können.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Calculator Form */}
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-sun-orange rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">Ihre Daten eingeben</h3>
                <p className="text-slate-500 text-sm">Für eine erste Einschätzung</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="consumption" className="text-base font-medium">Jährlicher Stromverbrauch *</Label>
                <div className="relative">
                  <Input
                    id="consumption"
                    type="number"
                    placeholder="z.B. 4500"
                    value={formData.consumption}
                    onChange={(e) => setFormData({ ...formData, consumption: e.target.value })}
                    className="h-14 text-lg pr-16 border-slate-200 focus:border-sun-orange focus:ring-sun-orange"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">kWh</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Dachausrichtung *</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, orientation: value })} value={formData.orientation}>
                  <SelectTrigger className="h-14 text-lg border-slate-200 focus:border-sun-orange focus:ring-sun-orange">
                    <SelectValue placeholder="Ausrichtung wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {roofOrientations.map((orientation) => (
                      <SelectItem key={orientation.value} value={orientation.value}>
                        {orientation.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="roofSize" className="text-base font-medium">Nutzbare Dachfläche (optional)</Label>
                <div className="relative">
                  <Input
                    id="roofSize"
                    type="number"
                    placeholder="z.B. 50"
                    value={formData.roofSize}
                    onChange={(e) => setFormData({ ...formData, roofSize: e.target.value })}
                    className="h-14 text-lg pr-16 border-slate-200 focus:border-sun-orange focus:ring-sun-orange"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">m²</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Elektroauto geplant?</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, hasEV: value })} value={formData.hasEV}>
                  <SelectTrigger className="h-14 text-lg border-slate-200 focus:border-sun-orange focus:ring-sun-orange">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Ja</SelectItem>
                    <SelectItem value="no">Nein</SelectItem>
                    <SelectItem value="maybe">Vielleicht später</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleCalculate}
                  disabled={!formData.consumption || !formData.orientation || isCalculating}
                  className="flex-1 h-14 bg-sun-orange hover:bg-sun-orange-dark text-white text-lg font-semibold shadow-lg shadow-sun-orange/25"
                >
                  {isCalculating ? (
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Calculator className="w-5 h-5 mr-2" />
                  )}
                  {isCalculating ? 'Berechne...' : 'Jetzt berechnen'}
                </Button>
                {result && (
                  <Button onClick={handleReset} variant="outline" className="h-14 px-6 border-slate-200">
                    <RefreshCw className="w-5 h-5" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className={`transition-all duration-500 ${result ? 'opacity-100 translate-y-0' : 'opacity-50 translate-y-4'}`}>
            <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-3xl p-8 text-white h-full">
              <h3 className="text-xl font-bold mb-8 flex items-center gap-3">
                <Zap className="w-6 h-6 text-sun-orange" />
                {result ? 'Ihre Empfehlung' : 'Ergebnis'}
              </h3>

              {result ? (
                <>
                  <div className="grid grid-cols-2 gap-6 mb-8">
                    <div className="bg-white/10 rounded-2xl p-6 text-center">
                      <Sun className="w-10 h-10 text-sun-orange mx-auto mb-3" />
                      <div className="text-3xl font-bold text-sun-orange">{result.pvSize} kWp</div>
                      <div className="text-white/70 text-sm mt-1">Empfohlene PV-Größe</div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6 text-center">
                      <Battery className="w-10 h-10 text-sun-blue mx-auto mb-3" />
                      <div className="text-3xl font-bold text-sun-blue">{result.batterySize} kWh</div>
                      <div className="text-white/70 text-sm mt-1">Empfohlener Speicher</div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6 text-center">
                      <Zap className="w-10 h-10 text-green-400 mx-auto mb-3" />
                      <div className="text-3xl font-bold text-green-400">{result.autonomyRate}%</div>
                      <div className="text-white/70 text-sm mt-1">Mögliche Autarkie</div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6 text-center">
                      <Euro className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                      <div className="text-3xl font-bold text-amber-400">{result.yearlySavings} €</div>
                      <div className="text-white/70 text-sm mt-1">Jährliche Ersparnis</div>
                    </div>
                  </div>

                  <div className="bg-sun-orange/20 border border-sun-orange/30 rounded-xl p-5 mb-6">
                    <p className="text-white/90 text-sm leading-relaxed">
                      <strong className="text-sun-orange">Hinweis:</strong> Dies ist eine erste Einschätzung. 
                      Für eine präzise Planung analysieren wir Ihr Dach vor Ort und erstellen eine 
                      individuelle Wirtschaftlichkeitsberechnung.
                    </p>
                  </div>

                  <Button
                    onClick={scrollToContact}
                    className="w-full h-14 bg-sun-orange hover:bg-sun-orange-dark text-white text-lg font-semibold"
                  >
                    Detaillierte Analyse anfragen
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-[calc(100%-4rem)] text-center">
                  <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mb-6">
                    <Calculator className="w-12 h-12 text-white/40" />
                  </div>
                  <p className="text-white/60 text-lg">
                    Geben Sie Ihre Daten ein und klicken Sie auf "Jetzt berechnen"
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AutarkieCalculator;
